import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, SITE } from "@/data/site";

export default function Header({ onBook }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#F9F6F0]/80 border-b border-[#E6DFD3]/60 py-3"
          : "bg-transparent py-5"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <a href="#accueil" className="flex flex-col group" data-testid="nav-logo">
          <span
            className={`font-display text-xl md:text-2xl leading-none transition-colors duration-500 ${
              scrolled ? "text-stone-900" : "text-[#F9F6F0]"
            }`}
          >
            Harmony
          </span>
          <span
            className={`text-[10px] tracking-[0.35em] uppercase font-body transition-colors duration-500 ${
              scrolled ? "text-[#8B5A2B]" : "text-[#C5A059]"
            }`}
          >
            Massage · {SITE.city}
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={`gold-underline text-sm font-light tracking-wide transition-colors duration-500 ${
                scrolled ? "text-stone-700 hover:text-stone-900" : "text-[#F9F6F0]/90 hover:text-white"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={onBook}
            data-testid="header-book-btn"
            className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#C5A059] text-white text-sm font-medium font-body hover:bg-[#A38044] transition-all duration-300 hover:-translate-y-0.5 shadow-md shadow-amber-900/10"
          >
            Réserver
          </button>
          <button
            className={`lg:hidden p-2 transition-colors ${scrolled ? "text-stone-900" : "text-white"}`}
            onClick={() => setMobileOpen(true)}
            aria-label="Menu"
            data-testid="mobile-menu-open"
          >
            <Menu strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="lg:hidden fixed inset-0 bg-[#F9F6F0] z-50 flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#E6DFD3]">
              <span className="font-display text-xl text-stone-900">Harmony</span>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-2 text-stone-900"
                aria-label="Fermer"
                data-testid="mobile-menu-close"
              >
                <X strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex flex-col px-6 py-10 gap-6">
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={() => setMobileOpen(false)}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.05 }}
                  className="font-display text-3xl text-stone-900"
                  data-testid={`mobile-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </motion.a>
              ))}
              <button
                onClick={() => {
                  setMobileOpen(false);
                  onBook();
                }}
                className="mt-6 px-6 py-3 rounded-full bg-[#C5A059] text-white font-body font-medium self-start"
                data-testid="mobile-book-btn"
              >
                Réserver maintenant
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
